import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, computed, forwardRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import { BehaviorSubject, catchError, combineLatest, debounceTime, filter, forkJoin, map, of, startWith, take, tap } from 'rxjs';

import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { DoctorsService } from '@hm/core/services/doctors.service';
import { VisitsService } from '@hm/core/services/visits.service';

import { EleksVisitsListComponent } from '@hm/ui/lists/eleks-visits-list/eleks-visits-list.component';

import { DobrobutClinic } from '@lib/core/models/dobrobut-entities.interface';
import { DoctorProfile } from '@lib/core/models/doctor.interface';
import { ModalActionTypes } from '@lib/core/models/modal-action-types.enum';
import { Visit } from '@lib/core/models/visit.interface';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

interface EleksAppointmentBindingControls {
  externalAppointmentId: FormControl<string | null>;
  clinicAddress: FormControl<string | null>;
  locationId: FormControl<string | null>;
  doctorName: FormControl<string | null>;
  doctorId: FormControl<string | null>;
  comment: FormControl<string | null>;
}

export interface EleksAppointmentBindingFormValue {
  externalAppointmentId: string | null;
  clinicAddress: string | null;
  locationId: string | null;
  doctorName: string | null;
  doctorId: string | null;
  comment: string | null;
}

@Component({
  selector: 'hm-eleks-appointment-binding-form',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    LoaderComponent,
    EleksVisitsListComponent,
    NgOptimizedImage,
    AsyncPipe,
  ],
  templateUrl: './eleks-appointment-binding-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EleksAppointmentBindingFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EleksAppointmentBindingFormComponent),
      multi: true,
    },
  ],
})
export class EleksAppointmentBindingFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly visitsService = inject(VisitsService);
  private readonly doctorsService = inject(DoctorsService);

  readonly type = input.required<ModalActionTypes>();
  readonly action = input<Visit | null>(null);
  readonly appointment = input<Visit | null>(null);

  protected readonly loading = signal<boolean>(false);
  protected readonly userId = computed<string>(() => (this.type() === ModalActionTypes.Create ? this.action()?.userId : this.appointment()?.userId));
  protected readonly syncing = signal<boolean>(false);
  protected readonly eleksVisitErrorMessage = signal<string | null>(null);
  protected readonly isFormValid = signal<boolean>(false);

  protected appointmentForm!: FormGroup<EleksAppointmentBindingControls>;
  protected dobrobutClinics: DobrobutClinic[] = [];
  protected clinicOptions: DobrobutClinic[] = [];
  protected doctors: DoctorProfile[] = [];
  protected doctorsOptions: DoctorProfile[] = [];
  protected selectedEleksVisit$ = this.visitsService.selectedEleksVisit$;

  private clinicAddressSearch$ = new BehaviorSubject<string | null>(null);
  private doctorSearch$ = new BehaviorSubject<string | null>(null);

  private onChange = (value: EleksAppointmentBindingFormValue | null) => {};
  private onTouched = () => {};

  protected get fc(): EleksAppointmentBindingControls {
    return this.appointmentForm.controls;
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.selectedEleksVisit$.next(null);
  }

  private initForm(): void {
    this.loading.set(true);
    this.appointmentForm = this.fb.group<EleksAppointmentBindingControls>({
      externalAppointmentId: this.fb.control(null),
      clinicAddress: this.fb.control(null, [Validators.required]),
      locationId: this.fb.control(null),
      doctorId: this.fb.control(null),
      doctorName: this.fb.control(null, [Validators.required]),
      comment: this.fb.control(null),
    });

    if (this.type() === ModalActionTypes.Edit) {
      this.appointmentForm.patchValue({
        externalAppointmentId: this.appointment()?.systemMetadata.external_appointment_id,
        clinicAddress: this.appointment()?.metadata.location,
        locationId: this.appointment()?.metadata.location_id,
        doctorId: this.appointment()?.metadata.doctor_id,
        doctorName: this.appointment()?.metadata.doctor_name,
        comment: null,
      });
    }

    this.watchFormChanges();
    this.initClinicAddressSearch();
    this.initDoctorsSearch();
    this.initSelectedVisitWatcher();
  }

  private watchFormChanges(): void {
    combineLatest([this.appointmentForm.valueChanges, this.appointmentForm.statusChanges])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([value, status]) => {
        this.isFormValid.set(status === 'VALID');
        this.onChange(value as EleksAppointmentBindingFormValue);
      });

    this.appointmentForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.onTouched();
    });
  }

  private initSelectedVisitWatcher() {
    this.loading.set(true);
    forkJoin([this.getDobrobutClinics(), this.getDoctors()]).subscribe(() => {
      this.loading.set(false);
      this.watchSelectedVisit();
    });
  }

  private watchSelectedVisit() {
    this.visitsService.selectedEleksVisit$
      .pipe(
        filter(value => !!value),
        tap(eleksVisit => {
          this.syncing.set(true);
          this.eleksVisitErrorMessage.set(null);

          if (eleksVisit.doctorName) {
            const selectedDoctor: DoctorProfile = this.doctors.find(doctor => doctor?.id === eleksVisit?.doctorId);
            if (selectedDoctor) this.selectDoctor(selectedDoctor.id, selectedDoctor.fullName);
            else this.selectDoctor(eleksVisit.doctorId, eleksVisit.doctorName);
          }
          if (eleksVisit.location) {
            const selectedDobrobutClinic: DobrobutClinic = this.dobrobutClinics.find(clinic => clinic.companyAddress === eleksVisit.location);
            this.selectClinicAddress(selectedDobrobutClinic);
          }
          this.fc.externalAppointmentId.setValue(eleksVisit.id);
          this.fc.clinicAddress.setValue(eleksVisit.location);
          this.syncing.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: error => {
          this.eleksVisitErrorMessage.set(error.error?.Message || 'Виникла помилка при синхронізації з Eleks');
          this.syncing.set(false);
        },
      });
  }

  protected searchClinicAddress(value: string) {
    return this.clinicAddressSearch$.next(value);
  }

  protected searchDoctor(value: string) {
    return this.doctorSearch$.next(value);
  }

  protected selectClinicAddress(event: DobrobutClinic) {
    this.fc.clinicAddress.setValue(event.companyAddress);
    this.fc.locationId.setValue(event.companyGuid);
    this.onTouched();
  }

  protected selectDoctor(doctorId: string, doctorName: string) {
    this.fc.doctorId.setValue(doctorId ?? null);
    this.fc.doctorName.setValue(doctorName ?? null);
    this.onTouched();
  }

  private getDobrobutClinics() {
    return this.visitsService.getDobrobutClinics().pipe(
      catchError(() => of<DobrobutClinic[]>([])),
      tap((clinics: DobrobutClinic[]) => (this.dobrobutClinics = clinics)),
      take(1)
    );
  }

  private getDoctors() {
    return this.doctorsService.getDoctors().pipe(
      catchError(() => of<DoctorProfile[]>([])),
      tap((doctors: DoctorProfile[]) => (this.doctors = doctors)),
      take(1)
    );
  }

  private initClinicAddressSearch() {
    this.appointmentForm
      .get('clinicAddress')
      .valueChanges.pipe(
        debounceTime(200),
        filter(value => !!value),
        startWith(''),
        map(value => {
          return this.dobrobutClinics.filter(clinic => clinic.companyAddress?.includes(value));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(res => {
        this.clinicOptions = res;
        this.loading.set(false);
      });
  }

  private initDoctorsSearch() {
    this.appointmentForm
      .get('doctorName')
      .valueChanges.pipe(
        debounceTime(200),
        filter(value => !!value),
        startWith(''),
        map(value => {
          return this.doctors.filter(doctor => doctor.fullName.includes(value));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(res => {
        this.doctorsOptions = res;
      });
  }

  writeValue(value: EleksAppointmentBindingFormValue | null): void {
    if (this.appointmentForm && value) {
      this.appointmentForm.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: EleksAppointmentBindingFormValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.appointmentForm) {
      if (isDisabled) {
        this.appointmentForm.disable();
      } else {
        this.appointmentForm.enable();
      }
    }
  }

  validate(): ValidationErrors | null {
    if (!this.appointmentForm) {
      return { invalidForm: true };
    }
    return this.appointmentForm.valid ? null : { invalidEleksBindingForm: true };
  }
}
